/**
 * CloseTaskWithTimeDialog — Iter · Feb 2026
 *
 * Fires whenever a user flips a task's status to `done`. Shows all
 * previously-logged time entries + total, and offers two paths:
 *
 *   1. Log more time for today, then close the task (single atomic call
 *      to POST /api/v1/tasks/{tid}/close-with-time).
 *   2. Mark done without logging — REQUIRES a short reason/note so the
 *      task history captures WHY zero effort was recorded.
 *
 * When the user picks path 2 AND the task's total logged hours = 0, a
 * second `window.confirm()` asks "Close without any hours?" as a final
 * guardrail. Regardless of the path, the backend appends a system
 * comment in the task thread so the audit trail is complete.
 *
 * The dialog is compact (max-w-lg, single scrollable body) and stacks
 * vertically on phones — no side-by-side grids that would squeeze the
 * time-entry list on 375px viewports.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Clock, CheckCircle2, AlertTriangle } from "lucide-react";

import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";

const _todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const _fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short" }); }
  catch { return iso; }
};

export default function CloseTaskWithTimeDialog({ open, task, onCancel, onDone }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hours, setHours] = useState("");
  const [note, setNote] = useState("");
  const [skipMode, setSkipMode] = useState(false);
  const [skipReason, setSkipReason] = useState("");

  const taskId = task?.id;
  const taskTitle = task?.title || "this task";

  useEffect(() => {
    if (!open || !taskId) return;
    let alive = true;
    setLoading(true);
    setEntries([]);
    setHours("");
    setNote("");
    setSkipMode(false);
    setSkipReason("");
    (async () => {
      try {
        const { data } = await api.get(`/tasks/${taskId}/time-entries`);
        if (alive) setEntries(Array.isArray(data) ? data : []);
      } catch { /* non-critical — modal still usable */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [open, taskId]);

  const totalHours = useMemo(
    () => entries.reduce((s, e) => s + (Number(e.hours) || 0), 0),
    [entries],
  );

  const parsedHours = Number.parseFloat(String(hours || "").trim());
  const hoursValid = Number.isFinite(parsedHours) && parsedHours > 0 && parsedHours <= 24;

  const submitWithTime = async () => {
    if (!hoursValid) { toast.error("Enter hours between 0.25 and 24"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${taskId}/close-with-time`, {
        hours: parsedHours,
        entry_date: _todayIso(),
        description: (note || "").trim(),
      });
      toast.success(`Logged ${parsedHours}h & marked "${taskTitle.slice(0, 40)}" as Done`);
      onDone && onDone(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not close task"));
    } finally {
      setBusy(false);
    }
  };

  const submitSkip = async () => {
    const reason = (skipReason || "").trim();
    if (!reason) { toast.error("Please share a short reason for skipping"); return; }
    // Final guardrail when the task will end with ZERO logged hours.
    if (totalHours <= 0) {
      const proceed = window.confirm(
        "This task will close with 0 logged hours. Are you sure?"
      );
      if (!proceed) return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${taskId}/close-with-time`, {
        hours: 0,
        skip_reason: reason,
      });
      toast.success(`Marked "${taskTitle.slice(0, 40)}" as Done`);
      onDone && onDone(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not close task"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v && !busy) onCancel && onCancel(); }}>
      <DialogContent
        className="max-w-lg w-[calc(100vw-2rem)] p-0 gap-0 max-h-[92vh] overflow-hidden flex flex-col rounded-none"
        data-testid="close-task-dialog"
      >
        <DialogHeader className="px-4 py-3 border-b border-border shrink-0">
          <DialogTitle className="text-sm flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            Close task &amp; log time
          </DialogTitle>
          <div className="text-xs text-muted-foreground truncate" title={taskTitle}>{taskTitle}</div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Existing time entries block */}
          <div className="border border-border" data-testid="close-task-history">
            <div className="px-3 py-1.5 bg-muted/40 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground flex items-center justify-between">
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Time logged so far</span>
              <span className="font-mono normal-case tracking-normal text-foreground/80">
                Total: <span className={totalHours > 0 ? "text-emerald-600 font-semibold" : "text-rose-600 font-semibold"} data-testid="close-task-total-hours">{totalHours.toFixed(2)}h</span>
              </span>
            </div>
            <div className="max-h-40 overflow-y-auto">
              {loading ? (
                <div className="px-3 py-2 text-xs text-muted-foreground italic">Loading…</div>
              ) : entries.length === 0 ? (
                <div className="px-3 py-2 text-xs text-muted-foreground italic flex items-center gap-2">
                  <AlertTriangle className="w-3 h-3 text-amber-500" />
                  No time entries yet on this task.
                </div>
              ) : (
                <ul className="divide-y divide-border" data-testid="close-task-entries">
                  {entries.map((e) => (
                    <li key={e.id} className="px-3 py-1.5 text-xs flex items-center gap-2">
                      <span className="text-muted-foreground w-14 shrink-0">{_fmtDate(e.entry_date)}</span>
                      <span className="font-mono w-14 shrink-0 text-right text-emerald-700">{Number(e.hours).toFixed(2)}h</span>
                      <span className="text-muted-foreground truncate flex-1" title={e.user_name}>{e.user_name || "—"}</span>
                      {e.description && <span className="text-muted-foreground/80 italic truncate max-w-[40%]" title={e.description}>{e.description}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {!skipMode ? (
            <>
              {/* Log more time */}
              <div className="space-y-2" data-testid="close-task-log-section">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Log time for today ({_todayIso()})
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="sm:w-32 shrink-0">
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Hours</Label>
                    <Input
                      type="number"
                      min="0.25"
                      step="0.25"
                      max="24"
                      value={hours}
                      onChange={(e) => setHours(e.target.value)}
                      placeholder="1.5"
                      className="h-9 text-sm"
                      autoFocus
                      data-testid="close-task-hours-input"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Note (optional)</Label>
                    <Input
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="What did you work on?"
                      className="h-9 text-sm"
                      data-testid="close-task-note-input"
                    />
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSkipMode(true)}
                className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-4"
                data-testid="close-task-skip-toggle"
              >
                Mark done without logging time
              </button>
            </>
          ) : (
            <div className="space-y-2 border border-amber-200 bg-amber-50/60 p-3" data-testid="close-task-skip-section">
              <div className="text-[10px] uppercase tracking-widest text-amber-700 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Closing without effort
              </div>
              <Label className="text-xs text-foreground">Please share why you&apos;re skipping the time log</Label>
              <Textarea
                value={skipReason}
                onChange={(e) => setSkipReason(e.target.value)}
                placeholder="e.g. Handed off to teammate · No work required · Duplicate of another task"
                className="min-h-[70px] text-sm"
                data-testid="close-task-skip-reason"
              />
              <button
                type="button"
                onClick={() => setSkipMode(false)}
                className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-4"
                data-testid="close-task-skip-cancel"
              >
                Back — I&apos;ll log time
              </button>
            </div>
          )}
        </div>

        <DialogFooter className="p-3 border-t border-border shrink-0 gap-2 flex-col sm:flex-row">
          <Button
            variant="ghost"
            onClick={() => onCancel && onCancel()}
            disabled={busy}
            className="rounded-none"
            data-testid="close-task-cancel-btn"
          >
            Cancel
          </Button>
          {!skipMode ? (
            <Button
              onClick={submitWithTime}
              disabled={busy || !hoursValid}
              className="rounded-none"
              data-testid="close-task-log-and-close-btn"
            >
              {busy ? "Saving…" : `Log ${hoursValid ? parsedHours.toFixed(2) : ""}h & mark Done`}
            </Button>
          ) : (
            <Button
              onClick={submitSkip}
              disabled={busy || !(skipReason || "").trim()}
              className="rounded-none bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="close-task-skip-confirm-btn"
            >
              {busy ? "Saving…" : "Close without logging"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
